import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../store/store';
import { updateProfile } from '../../store/reducers/userSlice';
import {
  setIsPaymentPopupOpen,
  setIsPaymentPopupFromBrandLimit,
} from '../../store/reducers/uiSlice';
import { fetchAppInit } from '../../lib/api/app-init';
import { HOME_ROUTE } from '../../lib/constants/route';
import ClientCard from './ClientCard';
import AddClientForm from './AddClientForm';
import BrandLimitUpgradePopup from '../../components/sidebar/BrandLimitUpgradePopup';
import make_package_subscriptions_api_call from '../../lib/api/ai-tracker/subscriptions/make_package_subscriptions_api_call';
import { createShop } from '../../lib/api/shop/create_shop';
import { setMessage } from '../../store/reducers/appSlice';
import { plusSignSVG } from '../../lib/allCustomSVGs';
import CustomSVGs from '../../ui/customSVGs/CustomSVGs';
import '../../styles/pages/clients/clients.scss';

const ClientsList: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const userProfile = useSelector((state: RootState) => state.user.profile);
  const taraSubscriptionDetails: any = useSelector(
    (state: RootState) => state.user?.shop?.taraSubscriptionDetails
  );
  const taraTrialDetails = useSelector(
    (state: RootState) => state.user?.shop?.taraTrialDetails
  ) || { is_trial: false, trial_available_days: undefined };

  const isActiveTrialWithDays =
    !!taraTrialDetails?.is_trial &&
    taraTrialDetails?.trial_available_days != null &&
    taraTrialDetails.trial_available_days > 0;

  const allShops = userProfile?.all_shops;
  const activeShopSlug = userProfile?.active_shop_slug;

  const [searchQuery, setSearchQuery] = useState('');
  const [isSwitching, setIsSwitching] = useState(false);
  const [switchingShopSlug, setSwitchingShopSlug] = useState<string | null>(
    null
  );
  const [showAddForm, setShowAddForm] = useState(false);
  const [isUpgradePopupOpen, setIsUpgradePopupOpen] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);

  const filteredShops = useMemo(() => {
    if (!allShops) return [];
    if (!searchQuery.trim()) {
      const active = allShops.find((shop) => shop.slug === activeShopSlug);
      const others = allShops.filter((shop) => shop.slug !== activeShopSlug);
      return active ? [active, ...others] : allShops;
    }
    return allShops.filter((shop) =>
      shop.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [allShops, searchQuery, activeShopSlug]);

  const handleShopSwitch = (shopSlug: string) => {
    if (isSwitching || shopSlug === activeShopSlug) return;

    setIsSwitching(true);
    setSwitchingShopSlug(shopSlug);

    setTimeout(() => {
      try {
        dispatch(updateProfile({ active_shop_slug: shopSlug }));
        navigate(HOME_ROUTE);
        fetchAppInit(true);
      } catch (error) {
        console.error('Error switching shop:', error);
      }
    }, 2000);
  };

  const handleAddNewClick = () => {
    const isAgency = userProfile?.role === 'agency';
    const seatCount = parseInt(
      taraSubscriptionDetails?.purchased_shops?.seat_count || '0'
    );
    const shopsCount = allShops?.length || 0;

    if (isAgency && shopsCount >= seatCount && !isActiveTrialWithDays) {
      setIsUpgradePopupOpen(true);
    } else {
      setShowAddForm(true);
    }
  };

  const handleBrandLimitReached = () => {
    setShowAddForm(false);
    dispatch(setIsPaymentPopupFromBrandLimit(true));
    setTimeout(() => {
      dispatch(setIsPaymentPopupOpen(true));
    }, 300);
  };

  const handleConfirmUpgrade = async () => {
    setIsUpgrading(true);
    try {
      const currentPackage = taraSubscriptionDetails?.package_slug;
      const currentSeatCount = parseInt(
        taraSubscriptionDetails?.purchased_shops?.seat_count || '0'
      );

      if (!currentPackage) {
        throw new Error('No active package found');
      }

      const upgradeRes = await make_package_subscriptions_api_call(
        currentPackage,
        currentSeatCount + 5
      );

      if (upgradeRes) {
        if (
          upgradeRes?.payment_details?.payment_details?._links?.checkout?.href
        ) {
          window.location.href =
            upgradeRes.payment_details.payment_details._links.checkout.href;
          return;
        }

        const tempName = `${userProfile?.email}'s shop`;
        const createRes = await createShop(tempName);

        if (createRes.success && createRes.data) {
          dispatch(
            updateProfile({ active_shop_slug: createRes.data.slug })
          );
          setIsUpgradePopupOpen(false);
          navigate(HOME_ROUTE);
          fetchAppInit(true);
        } else {
          dispatch(
            setMessage({
              message:
                createRes.message || 'Brand creation failed after upgrade',
              messageType: 'error',
            })
          );
        }
      } else {
        dispatch(
          setMessage({
            message: 'Subscription upgrade failed',
            messageType: 'error',
          })
        );
      }
    } catch (error: any) {
      console.error('Upgrade flow error:', error);
      dispatch(
        setMessage({
          message: error.message || 'An error occurred during upgrade',
          messageType: 'error',
        })
      );
    } finally {
      setIsUpgrading(false);
    }
  };

  return (
    <div className='clientsList'>
      <div className='clientsList__header'>
        <div className='clientsList__header-left'>
          <h1 className='clientsList__title'>Clients</h1>
          <span className='clientsList__count'>
            {allShops?.length || 0} {allShops?.length === 1 ? 'client' : 'clients'}
          </span>
        </div>
        <button
          type='button'
          className='clientsList__add-btn'
          onClick={handleAddNewClick}
          disabled={isSwitching}
        >
          <CustomSVGs svg={plusSignSVG} />
          <span>Add Client</span>
        </button>
      </div>

      {showAddForm && (
        <AddClientForm
          onClose={() => setShowAddForm(false)}
          onBrandLimitReached={handleBrandLimitReached}
        />
      )}

      <div className='clientsList__search'>
        <input
          type='text'
          placeholder='Search clients...'
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className='clientsList__search-input'
          disabled={isSwitching}
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className='clientsList__search-clear'
            type='button'
          >
            x
          </button>
        )}
      </div>

      {filteredShops.length === 0 ? (
        <div className='clientsList__empty'>
          {searchQuery
            ? `No clients matching "${searchQuery}"`
            : 'No clients yet. Add your first client to get started.'}
        </div>
      ) : (
        <div className='clientsList__grid'>
          {filteredShops.map((shop) => (
            <ClientCard
              key={shop.id}
              shop={shop}
              isActive={shop.slug === activeShopSlug}
              isSwitching={isSwitching}
              isSwitchingTarget={
                isSwitching && switchingShopSlug === shop.slug
              }
              onClick={() => handleShopSwitch(shop.slug)}
            />
          ))}
        </div>
      )}

      <BrandLimitUpgradePopup
        isOpen={isUpgradePopupOpen}
        onClose={() => setIsUpgradePopupOpen(false)}
        onConfirm={handleConfirmUpgrade}
        isLoading={isUpgrading}
      />
    </div>
  );
};

export default ClientsList;
